import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  formatTokenFailureMessage,
  getAccessTokenForApi,
} from '../services/auth0Metadata';
import { dispatchRefreshTokenError, isRefreshTokenError } from '../utils/errorHandling';

/** True only when org data loaded successfully and the user truly has no workspaces. */
export function shouldShowNoWorkspacesCard(opts: {
  loading: boolean;
  error: string | null;
  initialDataLoaded: boolean;
  organizations: Array<unknown> | null | undefined;
  invitedOrganizations: Array<unknown> | null | undefined;
}): boolean {
  const { loading, error, initialDataLoaded, organizations, invitedOrganizations } = opts;
  if (loading || error || !initialDataLoaded) return false;
  return (
    (!organizations || organizations.length === 0) &&
    (!invitedOrganizations || invitedOrganizations.length === 0)
  );
}

export interface Organization {
  id: string;
  name?: string;
  organization_name?: string;
  description?: string;
  createdAt?: string;
  api_keys?: {
    android?: string;
    ios?: string;
    web?: string;
  };
  policies?: any;
  has_policies?: boolean;
  policy_created_at?: string;
  policy?: any;
  invited?: boolean;
}

type InvitedOrganization = { 
  id: string; 
  name: string; 
  invited_at?: string; 
  invited_by?: string; 
  // Enrich invited orgs with fields if available from /organizations response
  api_keys?: { android?: string; ios?: string; web?: string };
  policies?: any;
  policy_created_at?: string;
  createdAt?: string;
};

interface OrganizationsContextValue {
  organizations: Organization[];
  invitedOrganizations: InvitedOrganization[];
  currentOrganizationId: string | null;
  enterprise: boolean; // Enterprise status from initial-data
  termsAcceptedVersion: string | null;
  privacyAcceptedVersion: string | null;
  initialDataLoaded: boolean;
  loading: boolean;
  error: string | null;
  setDefaultOrganization: (orgId: string) => Promise<void>;
  refetch: (silent?: boolean, force?: boolean) => Promise<void>;
  optimisticSetDefaultOrganization: (organizationId: string) => void;
}

const OrganizationsContext = createContext<OrganizationsContextValue | undefined>(undefined);

export const OrganizationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [invitedOrganizations, setInvitedOrganizations] = useState<InvitedOrganization[]>([]);
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(null);
  const [enterprise, setEnterprise] = useState<boolean>(false);
  const [termsAcceptedVersion, setTermsAcceptedVersion] = useState<string | null>(null);
  const [privacyAcceptedVersion, setPrivacyAcceptedVersion] = useState<string | null>(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const lastFetchAtRef = useRef<number>(0);
  const cooldownUntilRef = useRef<number>(0);

  const fetchOrganizationsAndInvites = useCallback(async (silent: boolean = false, force: boolean = false) => {
    if (!force && isFetchingRef.current) {
      return;
    }
    const now = Date.now();
    if (!force && now < (cooldownUntilRef.current || 0)) {
      if (!silent) setLoading(false);
      return;
    }
    if (!force && now - (lastFetchAtRef.current || 0) < 3000) {
      // Throttle to at most once per 3s (unless forced)
      if (!silent) setLoading(false);
      return;
    }
    const roles = Array.isArray((user as any)?.['https://bespot.com/roles']) ? (user as any)['https://bespot.com/roles'] : [];
    if (!isAuthenticated || !roles || roles.length === 0) {
      setOrganizations([]);
      setInvitedOrganizations([]);
      setCurrentOrganizationId(null);
      setLoading(false);
      return;
    }
    try {
      isFetchingRef.current = true;
      if (!silent) setLoading(true);
      setError(null);

      const token = await getAccessTokenForApi(getAccessTokenSilently);

      // Try batch endpoint first (reduces parallel requests and Auth0 calls)
      // Fallback to individual endpoints if batch endpoint fails
      let orgRes: any;
      let invitedRes: any;
      let batchDefaultOrgId: string | undefined;
      
      try {
        const batchRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/initial-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Use batch endpoint response
        const batchOrgs = batchRes.data.organizations || [];
        const batchInvitations = batchRes.data.invitations || [];
        const batchEnterprise = batchRes.data.enterprise || false;
        batchDefaultOrgId = batchRes.data?.default_organization_id as string | undefined;
        setTermsAcceptedVersion(batchRes.data?.terms_accepted_version || null);
        setPrivacyAcceptedVersion(batchRes.data?.privacy_accepted_version || null);
        
        // Set enterprise status from batch endpoint (reduces duplicate requests)
        setEnterprise(batchEnterprise);
        
        // If batch endpoint returns empty organizations but we expect to have some,
        // fall back to individual endpoints (batch endpoint might have cache issues)
        if (batchOrgs.length === 0 && batchInvitations.length === 0) {
          [orgRes, invitedRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/api/admin/organizations`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios
              .get(`${process.env.REACT_APP_API_URL}/api/admin/me/invitations`, {
                headers: { Authorization: `Bearer ${token}` },
              })
              .catch(() => ({ data: { invitations: [] } })),
          ]);
        } else {
          orgRes = { data: batchOrgs };
          invitedRes = { data: { invitations: batchInvitations } };
        }
      } catch (_batchError) {
        // Fallback to individual endpoints if batch endpoint fails
        [orgRes, invitedRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/admin/organizations`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios
            .get(`${process.env.REACT_APP_API_URL}/api/admin/me/invitations`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => ({ data: { invitations: [] } })),
        ]);
      }

      const fetched = (orgRes.data || []) as Organization[];
      const derivedInvitedFromOrgs: InvitedOrganization[] = fetched
        .filter(o => (o as any)?.invited === true)
        .map(o => ({ 
          id: o.id, 
          name: (o as any).name || (o as any).organization_name || (o as any).id,
          invited_by: (o as any).invited_by,
          invited_at: (o as any).invited_at,
          api_keys: (o as any).api_keys,
          external_ids: (o as any as any).external_ids,
          application_ids: (o as any as any).application_ids,
          policies: (o as any).policies,
          policy_created_at: (o as any).policy_created_at,
          createdAt: (o as any).createdAt,
        }));
      // Build invited list from endpoint

      // Invited list from /me/invitations
      const rawInvitations = invitedRes.data?.invitations || [];
      const invitedFromEndpoint: InvitedOrganization[] = rawInvitations.map((i: any) => {
        // Handle both 'id' and 'organization_id' field names
        const orgId = i.organization_id || i.id;
        // Handle both 'organization_name' and 'name' field names
        const orgName = i.organization_name || i.name || orgId;
        const mapped = {
          id: orgId,
          name: orgName,
          invited_at: i.invited_at,
          invited_by: i.invited_by,
        };
        return mapped;
      }).filter((i: InvitedOrganization) => {
        const hasId = !!i.id;
        return hasId;
      });

      // Fallback invited list from user.app_metadata.invited_organizations (if backend is rate-limited or endpoint empty)
      let invitedFromAppMeta: InvitedOrganization[] = [];
      try {
        const appMetaInvited = ((user as any)?.app_metadata?.invited_organizations || []) as any[];
        if (Array.isArray(appMetaInvited) && appMetaInvited.length > 0) {
          invitedFromAppMeta = appMetaInvited
            .map((o: any) => {
              const id = o?.id || o?.organization_id;
              if (!id) return null;
              const name = o?.name || o?.organization_name || id;
              const invited_by = o?.invited_by;
              const invited_at = o?.invited_at || o?.createdAt || o?.created_at;
              return { id, name, invited_at, invited_by } as InvitedOrganization;
            })
            .filter(Boolean) as InvitedOrganization[];
        }
      } catch {}

      // Merge invited lists (prefer endpoint names), unique by id
      const invitedMap = new Map<string, any>();
      // Seed with minimal endpoint entries
      for (const item of invitedFromEndpoint) invitedMap.set(item.id, { ...item });
      // Merge in app_metadata invited as fallback (do not override endpoint values)
      for (const item of invitedFromAppMeta) {
        if (!invitedMap.has(item.id)) {
          invitedMap.set(item.id, { ...item });
        } else {
          const existing = invitedMap.get(item.id);
          invitedMap.set(item.id, {
            ...item,
            name: existing.name || item.name,
            invited_at: existing.invited_at || item.invited_at,
            invited_by: existing.invited_by || item.invited_by,
          });
        }
      }
      // Merge in richer data from fetched where available
      for (const item of derivedInvitedFromOrgs) {
        const existing = invitedMap.get(item.id) || {};
        const existingName = existing.name as string | undefined;
        const existingNameIsId = !!existingName && existingName === item.id;
        invitedMap.set(item.id, { 
          ...existing,
          ...item,
          // Prefer a meaningful name over a raw ID placeholder from endpoint payloads.
          name: (!existingName || existingNameIsId) ? item.name : existingName,
        });
      }
      const invitedList = Array.from(invitedMap.values()) as InvitedOrganization[];

      // Treat any org present in invitations as invited (exclude from owned)
      const invitedIds = new Set(invitedList.map(i => i.id));
      let ownedOnly = fetched.filter(o => !(o as any)?.invited && !invitedIds.has(o.id));

      // Safety net: if everything got marked as invited by mistake, but backend returned orgs and
      // there are no explicit invitations, treat all as owned so the UI doesn't show 0
      if (ownedOnly.length === 0 && fetched.length > 0 && invitedList.length === 0) {
        ownedOnly = fetched;
      }

      // Hydrate from Auth0 user.app_metadata if server returned empty (429s or propagation lag)
      if (ownedOnly.length === 0) {
        try {
          const metaOrgs = ((user as any)?.app_metadata?.organizations || []) as any[];
          if (Array.isArray(metaOrgs) && metaOrgs.length > 0) {
            const hydrated = metaOrgs
              .map((o: any) => {
                const id = o?.id || o?.organization_id;
                if (!id) return null;
                const name = o?.name || o?.organization_name || id;
                const createdAt = o?.createdAt || o?.created_at || new Date().toISOString();
                const description = o?.organization_description || o?.description;
                return { id, name, createdAt, description } as Organization;
              })
              .filter(Boolean) as Organization[];
            if (hydrated.length > 0) {
              ownedOnly = hydrated;
            }
          }
        } catch {}
      }

      setOrganizations(ownedOnly);
      setInvitedOrganizations(invitedList);
      setInitialDataLoaded(true);
      

      // Determine selected org only when none is currently selected,
      // or when the current one no longer exists in available lists.
      const availableIds = new Set<string>([
        ...ownedOnly.map((o: any) => o.id),
        ...invitedList.map((i: any) => i.id),
      ]);

      let selectedId: string | null = currentOrganizationId && availableIds.has(currentOrganizationId)
        ? currentOrganizationId
        : null;

      if (!selectedId && batchDefaultOrgId && availableIds.has(batchDefaultOrgId)) {
        selectedId = batchDefaultOrgId;
      }

      if (!selectedId) {
        // Try user metadata default
        const appMetaDefault = (user as any)?.app_metadata?.default_organization_id as string | undefined;
        if (appMetaDefault && availableIds.has(appMetaDefault)) {
          selectedId = appMetaDefault;
        }
      }

      if (!selectedId) {
        // Try backend value
        try {
          const resp = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/admin/user/default-organization`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const backendId = resp.data?.default_organization_id as string | undefined;
          if (backendId && availableIds.has(backendId)) {
            selectedId = backendId;
          }
        } catch {
          // ignore
        }
      }

      if (!selectedId && ownedOnly.length > 0) {
        selectedId = ownedOnly[0].id;
      }
      
      // If no owned organizations but have invited organizations, select the first invited one
      if (!selectedId && ownedOnly.length === 0 && invitedList.length > 0) {
        selectedId = invitedList[0].id;
      }

      if (selectedId && selectedId !== currentOrganizationId) {
        setCurrentOrganizationId(selectedId);
        const evt = new CustomEvent('organizationChanged', {
          detail: { defaultOrganizationId: selectedId },
        });
        window.dispatchEvent(evt);
      }
      // If no owned orgs returned, enter a short cooldown to avoid tight loops under upstream rate limits
      if (ownedOnly.length === 0) {
        cooldownUntilRef.current = Date.now() + 15000; // 15s cooldown
      }
    } catch (e: any) {
      console.error('[OrganizationsContext] Failed to load organizations from batch and fallback endpoints', e);
      if (isRefreshTokenError(e)) {
        dispatchRefreshTokenError();
      }
      setError(formatTokenFailureMessage(e) || e?.message || 'Failed to load organizations');
      setOrganizations([]);
      setInvitedOrganizations([]);
      setInitialDataLoaded(false);
      // Back off on errors
      cooldownUntilRef.current = Date.now() + 15000;
    } finally {
      isFetchingRef.current = false;
      lastFetchAtRef.current = Date.now();
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, user, currentOrganizationId]);

  const setDefaultOrganization = useCallback(async (orgId: string) => {
    try {
      const token = await getAccessTokenForApi(getAccessTokenSilently);

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/user/default-organization`,
        { organization_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCurrentOrganizationId(orgId);
      // Dispatch event for listeners
      const evt = new CustomEvent('organizationChanged', {
        detail: { defaultOrganizationId: orgId },
      });
      window.dispatchEvent(evt);
    } catch (e) {
      // No global error surfacing here; callers can show UI messages if needed
    }
  }, [getAccessTokenSilently]);

  // Optimistic update for setting default organization
  const optimisticSetDefaultOrganization = useCallback((organizationId: string) => {
    setCurrentOrganizationId(organizationId);
    // Dispatch event for listeners
    const evt = new CustomEvent('organizationChanged', {
      detail: { defaultOrganizationId: organizationId },
    });
    window.dispatchEvent(evt);
  }, []);

  useEffect(() => {
    const roles = Array.isArray((user as any)?.['https://bespot.com/roles']) ? (user as any)['https://bespot.com/roles'] : [];
    if (!isAuthenticated || !roles || roles.length === 0) return;
    fetchOrganizationsAndInvites();
    // Listen for external organizationChanged to sync state
    const onOrgChanged = (event: Event) => {
      const ce = event as CustomEvent;
      const id = ce?.detail?.defaultOrganizationId as string | undefined;
      if (id && id !== currentOrganizationId) {
        setCurrentOrganizationId(id);
      }
    };
    window.addEventListener('organizationChanged', onOrgChanged as EventListener);
    return () => {
      window.removeEventListener('organizationChanged', onOrgChanged as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const value = useMemo<OrganizationsContextValue>(() => ({
    organizations,
    invitedOrganizations,
    currentOrganizationId,
    enterprise,
    termsAcceptedVersion,
    privacyAcceptedVersion,
    initialDataLoaded,
    loading,
    error,
    setDefaultOrganization,
    refetch: fetchOrganizationsAndInvites,
    optimisticSetDefaultOrganization,
  }), [organizations, invitedOrganizations, currentOrganizationId, enterprise, termsAcceptedVersion, privacyAcceptedVersion, initialDataLoaded, loading, error, setDefaultOrganization, fetchOrganizationsAndInvites, optimisticSetDefaultOrganization]);

  return (
    <OrganizationsContext.Provider value={value}>
      {children}
    </OrganizationsContext.Provider>
  );
};

export const useOrganizationsContext = (): OrganizationsContextValue => {
  const ctx = useContext(OrganizationsContext);
  if (!ctx) throw new Error('useOrganizationsContext must be used within OrganizationsProvider');
  return ctx;
};


